import { supabase } from '../lib/supabase';
import { CoordinatorPaymentConfig, CoordinatorPaymentSetupForm } from '../types/coordinatorPayment';

export class CoordinatorPaymentService {
  
  /**
   * Get payment configuration for a coordinator
   */
  static async getCoordinatorPaymentConfig(coordinatorEmail: string): Promise<CoordinatorPaymentConfig | null> {
    try {
      const { data, error } = await supabase
        .from('coordinator_payment_configs')
        .select('*')
        .eq('coordinator_email', coordinatorEmail)
        .eq('is_active', true)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching coordinator payment config:', error);
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error in getCoordinatorPaymentConfig:', error);
      return null;
    }
  }

  /**
   * Create or update payment configuration for a coordinator
   */
  static async saveCoordinatorPaymentConfig(
    coordinatorEmail: string,
    coordinatorName: string,
    config: CoordinatorPaymentSetupForm
  ): Promise<CoordinatorPaymentConfig> {
    try {
      // Check if config already exists
      const existingConfig = await this.getCoordinatorPaymentConfig(coordinatorEmail);

      const configData = {
        coordinator_email: coordinatorEmail,
        coordinator_name: coordinatorName,
        payment_gateway: config.payment_gateway,
        payfast_merchant_id: config.payfast_merchant_id || null,
        payfast_merchant_key: config.payfast_merchant_key || null,
        payfast_passphrase: config.payfast_passphrase || null,
        payfast_sandbox: config.payfast_sandbox ?? true,
        bank_name: config.bank_name || null,
        account_holder: config.account_holder || null,
        account_number: config.account_number || null,
        branch_code: config.branch_code || null,
        account_type: config.account_type || 'Cheque',
        auto_confirm_payments: config.auto_confirm_payments ?? false,
        payment_instructions: config.payment_instructions || null,
        is_active: true
      };

      let result;

      if (existingConfig) {
        // Update existing configuration
        const { data, error } = await supabase
          .from('coordinator_payment_configs')
          .update(configData)
          .eq('coordinator_email', coordinatorEmail)
          .select()
          .single();

        if (error) throw error;
        result = data;
      } else {
        // Create new configuration
        const { data, error } = await supabase
          .from('coordinator_payment_configs')
          .insert([configData])
          .select()
          .single();

        if (error) throw error;
        result = data;
      }

      console.log('Payment configuration saved successfully:', result);
      return result;
    } catch (error) {
      console.error('Error saving coordinator payment config:', error);
      throw new Error('Failed to save payment configuration');
    }
  }

  /**
   * Validate PayFast configuration
   */
  static validatePayFastConfig(config: CoordinatorPaymentSetupForm): string[] {
    const errors: string[] = [];

    if (config.payment_gateway === 'payfast' || config.payment_gateway === 'both') {
      if (!config.payfast_merchant_id) {
        errors.push('PayFast Merchant ID is required');
      }
      if (!config.payfast_merchant_key) {
        errors.push('PayFast Merchant Key is required');
      }
      if (!config.payfast_passphrase) {
        errors.push('PayFast Passphrase is required');
      }
    }

    return errors;
  }

  /**
   * Validate banking configuration
   */
  static validateBankingConfig(config: CoordinatorPaymentSetupForm): string[] {
    const errors: string[] = [];

    if (config.payment_gateway === 'manual' || config.payment_gateway === 'both') {
      if (!config.bank_name) {
        errors.push('Bank name is required');
      }
      if (!config.account_holder) {
        errors.push('Account holder name is required');
      }
      if (!config.account_number) {
        errors.push('Account number is required');
      }
      if (!config.branch_code) {
        errors.push('Branch code is required');
      }
    }

    return errors;
  }

  /**
   * Validate complete configuration
   */
  static validateConfiguration(config: CoordinatorPaymentSetupForm): string[] {
    const errors: string[] = [];

    // Validate payment gateway selection
    if (!config.payment_gateway) {
      errors.push('Please select a payment method');
    }

    // Validate PayFast config if selected
    errors.push(...this.validatePayFastConfig(config));

    // Validate banking config if selected
    errors.push(...this.validateBankingConfig(config));

    return errors;
  }

  /**
   * Get all active coordinator payment configurations (admin only)
   */
  static async getAllCoordinatorPaymentConfigs(): Promise<CoordinatorPaymentConfig[]> {
    try {
      const { data, error } = await supabase
        .from('coordinator_payment_configs')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return data || [];
    } catch (error) {
      console.error('Error fetching all coordinator payment configs:', error);
      throw error;
    }
  }

  /**
   * Deactivate a coordinator's payment configuration
   */
  static async deactivateCoordinatorPaymentConfig(coordinatorEmail: string): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('coordinator_payment_configs')
        .update({ is_active: false })
        .eq('coordinator_email', coordinatorEmail);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deactivating coordinator payment config:', error);
      return false;
    }
  }

  /**
   * Test PayFast configuration (basic validation)
   */
  static testPayFastConfiguration(config: CoordinatorPaymentSetupForm): { valid: boolean; message: string } {
    const errors = this.validatePayFastConfig(config);
    
    if (errors.length > 0) {
      return {
        valid: false,
        message: errors.join(', ')
      };
    }

    // Basic format validation
    if (config.payfast_merchant_id && config.payfast_merchant_id.length < 5) {
      return {
        valid: false,
        message: 'Merchant ID appears to be too short'
      };
    }

    if (config.payfast_merchant_key && config.payfast_merchant_key.length < 10) {
      return {
        valid: false,
        message: 'Merchant Key appears to be too short'
      };
    }

    return {
      valid: true,
      message: 'PayFast configuration appears valid'
    };
  }
} 